import { useState, useEffect } from "react";
import {
  HiOutlineCalendar, HiOutlineEnvelope, HiOutlinePhone, HiOutlineShieldCheck, HiOutlineStar,
  HiOutlineKey, HiOutlineDevicePhoneMobile, HiOutlineCheckCircle,
  HiOutlineEye, HiOutlineEyeSlash, HiOutlineLockClosed,
  HiOutlineMapPin, HiOutlineUser, HiOutlineUserGroup,
} from "react-icons/hi2";
import PortalLayout from "./PortalLayout";
import { portalAPI, portalAuth, ssoAPI, clearPortalToken } from "./portalApi";
import toast from "react-hot-toast";
import "./ProfilePage.css";

const TABS = ["Personal Info","Preferences","Security","Notifications","Privacy"];

// Journey progress now comes from the API (portalAPI.me() -> data.progress),
// computed from the member's real pipeline and booking state. It used to be
// this hardcoded list, which showed every member "Retreat Completed" and a
// fixed 60% regardless of where they actually were.


/* ═══════════════════════════════════════════════════════════
   SecurityTab — set/change password + sign-in methods
═══════════════════════════════════════════════════════════ */
function SecurityTab({ hasPassword, email, onPasswordSet }) {
  const [mode,       setMode]       = useState("view");    // view | set-pw | change-pw | otp-test
  const [pw,         setPw]         = useState("");
  const [pwConfirm,  setPwConfirm]  = useState("");
  const [showPw,     setShowPw]     = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [success,    setSuccess]    = useState("");
  const [error,      setError]      = useState("");

  const handleSetPassword = async () => {
    setError(""); setSuccess("");
    if (pw.length < 8)        { setError("Password must be at least 8 characters."); return; }
    if (pw !== pwConfirm)     { setError("Passwords do not match."); return; }
    setSaving(true);
    try {
      await portalAuth.setPassword(pw);
      setSuccess("Password saved! You can now sign in with your password.");
      onPasswordSet();
      setMode("view");
      setPw(""); setPwConfirm("");
    } catch (err) { setError(err.message || "Failed to save. Please try again."); }
    finally { setSaving(false); }
  };

  return (
    <div className="pc pp__info-col pp__security-tab">
      <p className="p-section-title">Sign-In Methods</p>
      <p className="pp__security-sub">
        Choose how you sign in to your Forge member portal.
        You can always use your email + a one-time code — it's the most secure option.
      </p>

      {/* Method 1 — OTP (always available) */}
      <div className="pp__method-card pp__method-card--active">
        <div className="pp__method-head">
          <div className="pp__method-icon pp__method-icon--green">
            <HiOutlineDevicePhoneMobile />
          </div>
          <div className="pp__method-info">
            <p className="pp__method-label">Sign-In Code (Email OTP)</p>
            <p className="pp__method-desc">A 6-digit code is sent to <strong>{email}</strong> each time you sign in.</p>
          </div>
          <span className="pp__method-badge pp__method-badge--active">Active</span>
        </div>
        <p className="pp__method-note">
          <HiOutlineShieldCheck /> This method is always available and cannot be disabled — it's your backup.
        </p>
      </div>

      {/* Method 2 — Password */}
      <div className={`pp__method-card ${hasPassword ? "pp__method-card--active" : ""}`}>
        <div className="pp__method-head">
          <div className={`pp__method-icon ${hasPassword ? "pp__method-icon--green" : "pp__method-icon--muted"}`}>
            <HiOutlineKey />
          </div>
          <div className="pp__method-info">
            <p className="pp__method-label">Password</p>
            <p className="pp__method-desc">
              {hasPassword
                ? "A password is set. You can sign in with your email + password."
                : "No password set. You can add one for quicker sign-in."}
            </p>
          </div>
          {hasPassword
            ? <span className="pp__method-badge pp__method-badge--active">Active</span>
            : <span className="pp__method-badge pp__method-badge--off">Not set</span>}
        </div>

        {/* Set / Change password form */}
        {mode === "view" && (
          <button type="button" className="p-btn p-btn--outline pp__method-btn"
            onClick={() => { setMode("set-pw"); setError(""); setSuccess(""); }}>
            {hasPassword ? "Change Password" : "Set a Password"}
          </button>
        )}

        {(mode === "set-pw" || mode === "change-pw") && (
          <div className="pp__pw-form">
            <div className="pp__pw-field">
              <label className="pp__field-label">{hasPassword ? "New Password" : "Create Password"}</label>
              <div className="pp__pw-input-wrap">
                <input type={showPw ? "text" : "password"}
                  className="pp__pw-input" placeholder="Minimum 8 characters"
                  value={pw} onChange={e => { setPw(e.target.value); setError(""); }}
                  autoComplete="new-password" />
                <button type="button" className="pp__pw-eye" onClick={() => setShowPw(v => !v)}>
                  {showPw ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
                </button>
              </div>
            </div>
            <div className="pp__pw-field">
              <label className="pp__field-label">Confirm Password</label>
              <input type="password" className="pp__pw-input"
                placeholder="Repeat your password"
                value={pwConfirm} onChange={e => { setPwConfirm(e.target.value); setError(""); }}
                autoComplete="new-password" />
            </div>
            {/* Strength indicator */}
            {pw && (
              <div className="pp__pw-strength">
                {[1,2,3,4].map(i => (
                  <div key={i} className={`pp__pw-bar ${
                    pw.length >= i*3 &&
                    (i < 3 || /[A-Z]/.test(pw)) &&
                    (i < 4 || /[0-9!@#$%^&*]/.test(pw))
                      ? "pp__pw-bar--filled" : ""
                  }`} />
                ))}
                <span className="pp__pw-strength-label">
                  {pw.length < 6  ? "Too short" :
                   pw.length < 10 ? "Fair" :
                   /[A-Z]/.test(pw) && /[0-9]/.test(pw) ? "Strong" : "Good"}
                </span>
              </div>
            )}
            {error   && <p className="pp__pw-error">{error}</p>}
            {success && <p className="pp__pw-success"><HiOutlineCheckCircle /> {success}</p>}
            <div className="pp__pw-actions">
              <button type="button" className="p-btn p-btn--outline"
                onClick={() => { setMode("view"); setPw(""); setPwConfirm(""); setError(""); }}>
                Cancel
              </button>
              <button type="button" className="p-btn p-btn--fill"
                onClick={handleSetPassword} disabled={saving || !pw || !pwConfirm}>
                {saving ? "Saving…" : hasPassword ? "Update Password" : "Save Password"}
              </button>
            </div>
          </div>
        )}
      </div>

      <SharedSignIn email={email} />
    </div>
  );
}

/* One sign-in with Makua Retreats, and signing out everywhere. */
function SharedSignIn({ email }) {
  const [busy, setBusy] = useState("");
  const goMakua = async () => {
    setBusy("go");
    try { const r = await ssoAPI.handoff(); window.location.assign(r.data.url); }
    catch (err) { toast.error(err.message || "Couldn't open Makua just now."); setBusy(""); }
  };
  const outEverywhere = async () => {
    if (!window.confirm("Sign out on every device, here and on Makua Retreats?")) return;
    setBusy("out");
    try { const r = await ssoAPI.logoutEverywhere(); toast.success(r.message); clearPortalToken(); window.location.href = "/portal/login"; }
    catch (err) { toast.error(err.message); setBusy(""); }
  };
  return (
    <>
      <div className="pp__method-card">
        <div className="pp__method-head">
          <div className="pp__method-icon pp__method-icon--muted"><HiOutlineUserGroup /></div>
          <div className="pp__method-info">
            <p className="pp__method-label">Makua Retreats</p>
            <p className="pp__method-desc">Your sign-in also works at Makua Retreats, our sister retreat centre, as <strong>{email}</strong>. Only your name and email go with you, and only when you agree; nothing from your Forge application is shared.</p>
          </div>
        </div>
        <button type="button" className="p-btn p-btn--outline pp__method-btn" disabled={!!busy} onClick={goMakua}>
          {busy === "go" ? "Opening…" : "Open Makua with this sign-in"}
        </button>
      </div>
      <div className="pp__method-card">
        <div className="pp__method-head">
          <div className="pp__method-icon pp__method-icon--muted"><HiOutlineLockClosed /></div>
          <div className="pp__method-info">
            <p className="pp__method-label">Sign out everywhere</p>
            <p className="pp__method-desc">Ends your sign-in on every phone and computer, on Forge and on Makua. Use it if you lost a device or signed in somewhere shared.</p>
          </div>
        </div>
        <button type="button" className="p-btn p-btn--outline pp__method-btn" disabled={!!busy} onClick={outEverywhere}>
          {busy === "out" ? "Signing out…" : "Sign out everywhere"}
        </button>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   PreferencesTab — backs the Preferences / Notifications /
   Privacy tabs, which were "coming soon" placeholders.
   All three render from the same saved preferences record;
   `section` decides which subset of fields is shown.
═══════════════════════════════════════════════════════════ */
function PreferencesTab({ section }) {
  const [prefs,   setPrefs]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    portalAPI.getPreferences()
      .then(res => setPrefs(res?.data || null))
      .catch(() => setPrefs(null))
      .finally(() => setLoading(false));
  }, []);

  // Optimistic toggle — flip locally, persist, roll back on failure.
  const toggle = async (field) => {
    const next = { ...prefs, [field]: prefs[field] ? 0 : 1 };
    setPrefs(next);
    setSaving(true);
    try {
      await portalAPI.updatePreferences({ [field]: !!next[field] });
    } catch (err) {
      setPrefs(prefs);
      toast.error(err.message || "Couldn't save that.");
    } finally { setSaving(false); }
  };

  const saveText = async (field, value) => {
    setSaving(true);
    try {
      const res = await portalAPI.updatePreferences({ [field]: value });
      setPrefs(res?.data || prefs);
      toast.success("Saved.");
    } catch (err) { toast.error(err.message || "Couldn't save that."); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="pc pp__prefs"><p className="pp__prefs-loading">Loading…</p></div>;
  if (!prefs)  return <div className="pc pp__prefs"><p className="pp__prefs-loading">Couldn't load your preferences.</p></div>;

  const NOTIFY = [
    ["notify_email",      "Notes from the team",   "Emails when our team sends you a message or replies."],
    ["notify_retreat",    "Retreat updates",        "Offers, deadlines, and reminders about your retreat."],
    ["notify_board",      "Message board replies",  "When someone replies to a post you made."],
    ["notify_newsletter", "General updates",        "Occasional news from Forge Retreat Colombia."],
  ];

  const PRIVACY = [
    ["show_in_cohort", "Show me on cohort rosters", "Other confirmed attendees can see your first name and last initial."],
    ["show_category",  "Show my background",        "Display whether you're a Veteran, First Responder, and so on."],
    ["show_location",  "Show my location",          "Display your city and country to other members."],
  ];

  const rows = section === "Notifications" ? NOTIFY : section === "Privacy" ? PRIVACY : null;

  return (
    <div className="pc pp__prefs">
      {rows ? (
        <>
          <p className="pp__prefs-intro">
            {section === "Notifications"
              ? "Choose what we email you about. We'll always contact you about anything urgent regarding your application or retreat."
              : "Control what other members can see about you. Our team can always see your full details."}
          </p>
          {rows.map(([field, label, hint]) => (
            <label key={field} className="pp__pref-row">
              <div className="pp__pref-copy">
                <span className="pp__pref-label">{label}</span>
                <span className="pp__pref-hint">{hint}</span>
              </div>
              <input type="checkbox" className="pp__pref-toggle"
                checked={!!prefs[field]} disabled={saving}
                onChange={() => toggle(field)} />
            </label>
          ))}
        </>
      ) : (
        <>
          <p className="pp__prefs-intro">
            Anything here helps our team prepare for your retreat. Leave blank if it doesn't apply.
          </p>
          <PrefTextField label="Preferred name"
            hint="What you'd rather be called, if different from your first name."
            value={prefs.preferred_name || ""} disabled={saving}
            onSave={v => saveText("preferred_name", v)} />
          <PrefTextField label="Dietary needs" multiline
            hint="Allergies, restrictions, or anything we should plan around at meals."
            value={prefs.dietary_notes || ""} disabled={saving}
            onSave={v => saveText("dietary_notes", v)} />
          <PrefTextField label="Accessibility needs" multiline
            hint="Mobility, medical, or other needs so we can plan properly."
            value={prefs.accessibility_notes || ""} disabled={saving}
            onSave={v => saveText("accessibility_notes", v)} />
        </>
      )}
    </div>
  );
}

/** Text/textarea field that only shows Save once the value actually changes. */
function PrefTextField({ label, hint, value, onSave, disabled, multiline }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const dirty = draft !== value;

  return (
    <div className="pp__pref-text">
      <label className="pp__pref-label">{label}</label>
      {hint && <p className="pp__pref-hint">{hint}</p>}
      {multiline ? (
        <textarea className="pp__pref-input" rows={3} value={draft}
          disabled={disabled} onChange={e => setDraft(e.target.value)} />
      ) : (
        <input className="pp__pref-input" value={draft}
          disabled={disabled} onChange={e => setDraft(e.target.value)} />
      )}
      {dirty && (
        <div className="pp__pref-save">
          <button type="button" className="p-btn p-btn--outline"
            onClick={() => setDraft(value)}>Cancel</button>
          <button type="button" className="p-btn p-btn--fill"
            disabled={disabled} onClick={() => onSave(draft)}>Save</button>
        </div>
      )}
    </div>
  );
}

export default function ProfilePage() {
  const [tab,    setTab]    = useState("Personal Info");
  const [member, setMember] = useState(null);
  const [hasPassword, setHasPassword] = useState(false);
  const progressItems = member?.progress || [];
  const pct = progressItems.length
    ? Math.round((progressItems.filter(i => i.done).length / progressItems.length) * 100)
    : 0;

  useEffect(() => {
    portalAPI.me()
      .then(res => {
        setMember(res?.data || null);
        setHasPassword(!!res?.data?.hasPassword);
      })
      .catch(() => {});
  }, []);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"}) : "—";

  const u = {
    firstName:        member?.firstName        || "…",
    lastName:         member?.lastName         || "",
    email:            member?.email            || "…",
    phone:            member?.phone            || "—",
    category:         member?.category         || "—",
    memberSince:      member?.submittedAt      ? new Date(member.submittedAt).toLocaleDateString("en-US",{year:"numeric",month:"long"}) : "—",
    memberId:         member?.appRef           || "—",
    // Extended fields from application record
    dob:              member?.dob              ? fmtDate(member.dob) : "Not provided",
    address:          [member?.address, member?.city, member?.state, member?.country].filter(Boolean).join(", ") || "Not provided",
    branch:           member?.branch           || member?.agency || "Not provided",
    employment:       member?.employmentStatus || "Not provided",
    ecName:           member?.ecName           || "Not provided",
    ecPhone:          member?.ecPhone          || "Not provided",
    ecRelationship:   member?.ecRelationship   || "Not provided",
  };

  return (
    <PortalLayout eyebrow="Profile" pageTitle="My Profile"
      quote='The greatest journey begins with knowing who you are.' quoteAuthor="Forge">
      <p className="pp__sub">Manage your personal information, preferences, and account settings.</p>

      {/* Profile header */}
      <div className="pc pp__header">
        <div className="pp__avatar-wrap">
          <div className="pp__avatar-lg">{u.firstName[0]}{u.lastName[0]}</div>
        </div>
        <div className="pp__header-info">
          <div className="pp__header-name-row">
            <h2 className="pp__name">{u.firstName} {u.lastName}</h2>
            <span className="pbadge pbadge--green">Verified ✓</span>
          </div>
          <div className="pp__header-meta">
            <span><HiOutlineShieldCheck />{u.category}</span>
            <span><HiOutlineCalendar />Member since {u.memberSince}</span>
            <span><HiOutlineEnvelope />{u.email}</span>
            <span><HiOutlinePhone />{u.phone}</span>
          </div>
        </div>
        <div className="pp__edit-locked">
          <HiOutlineLockClosed style={{fontSize:"0.8rem"}}/>
          <span>To update your info, contact <a href="mailto:support@forge.ngo">support@forge.ngo</a></span>
        </div>
      </div>

      {/* Tabs */}
      <div className="pp__tabs">
        {TABS.map(t => (
          <button key={t} type="button" className={`pp__tab ${tab===t?"pp__tab--active":""}`} onClick={()=>setTab(t)}>{t}</button>
        ))}
      </div>

      {tab === "Personal Info" && (
        <div className="pp__info-grid">
          {/* Personal info */}
          <div className="pc pp__info-col">
            <div className="pp__info-col-head">
              <p className="p-section-title">Personal Information</p>
              <span className="pp__info-locked-badge"><HiOutlineLockClosed/> View only</span>
            </div>
            <p className="pp__info-locked-note">
              To update your personal information, please contact us at{" "}
              <a href="mailto:support@forge.ngo">support@forge.ngo</a>.
            </p>
            <div className="pp__fields">
              {[
                ["Full Name",        u.firstName+" "+u.lastName, HiOutlineUser],
                ["Date of Birth",    u.dob,                      HiOutlineCalendar],
                ["Phone Number",     u.phone,                    HiOutlinePhone],
                ["Email Address",    u.email,                    HiOutlineEnvelope],
                ["Address",          u.address,                  HiOutlineMapPin],
                ["Branch / Agency",  u.branch,                   HiOutlineShieldCheck],
              ].map(([l, v, Icon]) => (
                <div key={l} className="pp__field">
                  <div className="pp__field-label-row">
                    <Icon className="pp__field-icon" aria-hidden="true"/>
                    <p className="pp__field-label">{l}</p>
                  </div>
                  <p className="pp__field-val">{v}</p>
                </div>
              ))}
            </div>

            {/* Emergency Contact */}
            <div className="pp__ec-block">
              <div className="pp__info-col-head" style={{marginBottom:"0.65rem"}}>
                <p className="p-section-title" style={{margin:0}}>Emergency Contact</p>
                <HiOutlineUserGroup style={{color:"#6b6258"}}/>
              </div>
              {[
                ["Name",         u.ecName],
                ["Phone",        u.ecPhone],
                ["Relationship", u.ecRelationship],
              ].map(([l,v]) => (
                <div key={l} className="pp__field pp__field--inline">
                  <p className="pp__field-label">{l}</p>
                  <p className="pp__field-val">{v}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Membership + progress */}
          <div className="pp__right-col">
            <div className="pc pp__membership">
              <p className="p-section-title">Membership</p>
              <div className="pp__membership-rows">
                {[
                  ["Membership Level", member?.hasAttendedRetreat ? "Alumni" : "Forge Member", true],
                  ["Member Since", u.memberSince, false],
                  ["Membership ID", u.memberId, false],
                ].map(([l,v,rust])=>(
                  <div key={l} className="pp__membership-row">
                    <span className="pp__field-label">{l}</span>
                    <span className={`pp__field-val ${rust?"pp__val--rust":""}`}>{v}</span>
                  </div>
                ))}
              </div>
              {member?.hasAttendedRetreat && (
                <div className="pp__alumni-note">
                  <HiOutlineStar /> You've completed a retreat — you're part of the Forge alumni community.
                </div>
              )}
            </div>

            <div className="pc pp__progress">
              <p className="p-section-title">Your Journey Progress</p>
              <div className="pp__progress-layout">
                {/* Circle */}
                <div className="pp__progress-circle">
                  <svg viewBox="0 0 80 80" className="pp__circle-svg">
                    <circle cx="40" cy="40" r="32" fill="none" stroke="var(--portal-border)" strokeWidth="6" />
                    <circle cx="40" cy="40" r="32" fill="none" stroke="var(--portal-forest)" strokeWidth="6"
                      strokeDasharray={`${2*Math.PI*32}`}
                      strokeDashoffset={`${2*Math.PI*32*(1-pct/100)}`}
                      strokeLinecap="round"
                      transform="rotate(-90 40 40)"
                    />
                  </svg>
                  <div className="pp__circle-pct">{pct}%<span>Complete</span></div>
                </div>
                {/* Items */}
                <div className="pp__progress-items">
                  {progressItems.map(item => (
                    <div key={item.label} className="pp__progress-item">
                      <div className={`p-dot ${item.done?"p-dot--done":item.partial?"p-dot--active":""}`}>
                        {item.done && "✓"}
                      </div>
                      <span className={`pp__progress-label ${item.partial?"pp__progress-label--partial":""}`}>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Security tab */}
      {tab === "Security" && (
        <SecurityTab
          hasPassword={hasPassword}
          email={u.email}
          onPasswordSet={() => setHasPassword(true)}
        />
      )}

      {/* Preferences / Notifications / Privacy — all backed by the same
          saved preferences record, rendered per section. */}
      {tab !== "Personal Info" && tab !== "Security" && (
        <PreferencesTab section={tab} />
      )}

      {/* Security note */}
      <div className="pc pp__security">
        <div className="p-icon-circle p-icon-circle--forest" style={{width:"3rem",height:"3rem"}}><HiOutlineShieldCheck /></div>
        <div><p className="pp__sec-title">Your security matters</p><p className="pp__sec-sub">We use industry-leading security to protect your account and information and privacy.</p></div>
        <button type="button" className="p-btn p-btn--outline">Manage Security</button>
        <img src="./assets/mountainsForst.png" alt="" className="pp__sec-mtn" />
      </div>
    </PortalLayout>
  );
}